import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Info } from 'lucide-react';
import type { CampaignFormData, CampaignGoal, CampaignPlatform, CampaignFormStep1Props } from './campaignFormTypes';
import { getCurrencySymbol } from '@/utils/currency';

export const CampaignFormStep1: React.FC<CampaignFormStep1Props> = ({
  formData,
  isBrandOrg,
  brands,
  brandsLoading,
  currentOrgName,
  createLoading,
  createError,
  userCountry,
  onUpdateFormData,
  onEndDateTouched,
  onNext,
  onClose,
  onAddBrandClick,
}) => {
  const handlePlatformToggle = (platform: CampaignPlatform) => {
    const updatedPlatforms = formData.platforms.includes(platform)
      ? formData.platforms.filter(p => p !== platform)
      : [...formData.platforms, platform];
    onUpdateFormData('platforms', updatedPlatforms);
  };

  return (
    <div className="space-y-4">

      {/* Campaign Name */}
      <div>
        <Input
          value={formData.name}
          onChange={(e) => onUpdateFormData('name', e.target.value)}
          placeholder="Name your campaign"
          className="w-full border-blue-300 focus:border-blue-500 rounded-md h-12 text-base placeholder:text-gray-400"
        />
      </div>

      {/* Brand Name */}
      <div>
        {isBrandOrg ? (
          <Input
            value={
              brandsLoading
                ? ''
                : (
                  brands?.find(b => b.id === formData.brandId)?.name
                  ?? brands?.[0]?.name
                  ?? currentOrgName
                  ?? ''
                )
            }
            disabled
            placeholder={brandsLoading ? 'Loading brand...' : 'Brand'}
            className="w-full border-blue-300 rounded-md h-12 text-base text-gray-600 bg-gray-50"
          />
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="min-w-0 flex-1">
              <Select
                value={formData.brandId}
                onValueChange={(value) => onUpdateFormData('brandId', value)}
              >
                <SelectTrigger className="w-full border-blue-300 rounded-md h-12 text-base text-gray-600">
                  <SelectValue placeholder="Brand Name" />
                </SelectTrigger>
                <SelectContent>
                  {brandsLoading ? (
                    <div className="p-2 text-sm text-gray-500">Loading brands...</div>
                  ) : brands?.length ? (
                    brands.map(brand => (
                      <SelectItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="p-2 text-sm text-gray-500">No brands found.</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onAddBrandClick}
              className="sm:mt-1 shrink-0"
            >
              Add Brand
            </Button>
          </div>
        )}
      </div>

      {/* Hashtags Textarea */}
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">
          Set hashtags, mentions, or keywords to track content
        </Label>
        <Textarea
          value={formData.hashtags}
          onChange={(e) => onUpdateFormData('hashtags', e.target.value)}
          placeholder="#hashtags, @mentions, keywords"
          className="w-full min-h-[160px] border-gray-200 rounded-md resize-y p-4 text-gray-600 placeholder:text-gray-400 focus:border-blue-500"
        />
      </div>

      {/* Checkboxes */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <Checkbox
            id="upgradeAlerts"
            checked={formData.upgradeAlerts}
            onCheckedChange={(c) => onUpdateFormData('upgradeAlerts', !!c)}
            className="border-gray-300 rounded-sm w-4 h-4"
          />
          <label htmlFor="upgradeAlerts" className="flex items-center text-sm text-gray-600 cursor-pointer">
            <span className="bg-black text-white text-[10px] font-bold px-1.5 py-0.5 rounded mr-2 tracking-wider">UPGRADE</span>
            your plan to enable alerts hashtags, mentions, or keywords
            <Info className="w-4 h-4 ml-1.5 text-gray-400" />
          </label>
        </div>

        <div className="flex items-center space-x-3">
          <Checkbox
            id="flagMissingAdDisclosure"
            checked={formData.flagMissingAdDisclosure}
            onCheckedChange={(c) => onUpdateFormData('flagMissingAdDisclosure', !!c)}
            className="border-gray-300 rounded-sm w-4 h-4"
          />
          <label htmlFor="flagMissingAdDisclosure" className="flex items-center text-sm text-gray-600 cursor-pointer">
            Flag posts missing ad disclosure
            <Info className="w-4 h-4 ml-1.5 text-gray-400" />
          </label>
        </div>

        <div className="flex items-center space-x-3">
          <Checkbox
            id="enableEventMode"
            checked={formData.enableEventMode}
            onCheckedChange={(c) => onUpdateFormData('enableEventMode', !!c)}
            className="border-gray-300 rounded-sm w-4 h-4"
          />
          <label htmlFor="enableEventMode" className="flex items-center text-sm text-gray-600 cursor-pointer">
            Enable Event mode to track all content from selected creators in a separate tab. This halves your monitoring limits.
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8">
        <div className="space-y-8">
          {/* Campaign Type */}
          <div>
            <Label className="text-sm font-bold text-gray-900 mb-3 block">Campaign Type</Label>
            <RadioGroup
              value={formData.campaignType}
              onValueChange={(val) => onUpdateFormData('campaignType', val as CampaignGoal)}
              className="flex flex-wrap items-center gap-x-6 gap-y-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="awareness" id="type-awareness" className="border-gray-300" />
                <Label htmlFor="type-awareness" className="text-sm text-gray-700 font-normal cursor-pointer">Awareness</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="engagement" id="type-engagement" className="border-gray-300" />
                <Label htmlFor="type-engagement" className="text-sm text-gray-700 font-normal cursor-pointer">Engagement</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="conversion" id="type-conversion" className="border-gray-300" />
                <Label htmlFor="type-conversion" className="text-sm text-gray-700 font-normal cursor-pointer">Conversion</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Platforms */}
          <div>
            <Label className="text-sm font-bold text-gray-900 mb-3 block">Platforms</Label>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <label className="flex items-center space-x-2 cursor-pointer">
                <Checkbox
                  checked={formData.platforms.includes('instagram')}
                  onCheckedChange={() => handlePlatformToggle('instagram')}
                  className="border-gray-300 rounded-sm w-4 h-4"
                />
                <span className="text-sm text-gray-700">Instagram</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <Checkbox
                  checked={formData.platforms.includes('youtube')}
                  onCheckedChange={() => handlePlatformToggle('youtube')}
                  className="border-gray-300 rounded-sm w-4 h-4"
                />
                <span className="text-sm text-gray-700">YouTube</span>
              </label>
            </div>
          </div>

          {/* Budget */}
          <div>
            <Label htmlFor="totalBudget" className="text-sm font-bold text-gray-900 mb-3 block">Budget ({getCurrencySymbol(userCountry)})</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none">
                {getCurrencySymbol(userCountry)}
              </span>
              <Input
                id="totalBudget"
                type="number"
                min="0"
                placeholder="Enter total campaign budget"
                value={formData.totalBudget}
                onChange={(e) => onUpdateFormData('totalBudget', e.target.value)}
                className="w-full border-gray-300 rounded-md h-12 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 pl-8"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              (Optional) Enter the total budget allocated for this campaign in {getCurrencySymbol(userCountry) === '₹' ? 'Indian Rupees (₹)' : 'US Dollars ($)'}.
            </p>
          </div>
        </div>

        {/* Dates */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="startDate" className="text-sm font-bold text-gray-900 mb-3 block">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={formData.startDate}
              onChange={(e) => onUpdateFormData('startDate', e.target.value)}
              className="w-full border-gray-200 rounded-md h-11 text-sm text-gray-700 focus:border-blue-500"
            />
          </div>
          <div>
            <Label htmlFor="endDate" className="text-sm font-bold text-gray-900 mb-3 block">End Date</Label>
            <Input
              id="endDate"
              type="date"
              value={formData.endDate}
              onChange={(e) => {
                onEndDateTouched();
                onUpdateFormData('endDate', e.target.value);
              }}
              className="w-full border-gray-200 rounded-md h-11 text-sm text-gray-700 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="pt-6 border-t border-gray-100 flex justify-between items-center mt-8">
        <Button variant="ghost" onClick={onClose} className="text-gray-500">
          Cancel
        </Button>
        <div className="flex items-center space-x-3">
          {createError && <span className="text-sm text-red-500">{createError}</span>}
          <Button
            onClick={onNext}
            disabled={createLoading || !formData.name || (!formData.brandId && !isBrandOrg)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-8"
          >
            Next
          </Button>
        </div>
      </div>

    </div>
  );
};
